import type { Rng, RoundOutcome } from '../../core/contracts'

/**
 * Stroop - избирательное внимание и торможение.
 *
 * Слово называет один цвет, написано другим. Нужно назвать цвет
 * ЧЕРНИЛ, подавив автоматическое чтение слова. Классическая проба
 * на контроль внимания.
 *
 * Раунд здесь - блок из нескольких проб, а не одна проба. Причина
 * в скоринге: по одной пробе точность равна 0 или 1, адаптиву не за
 * что зацепиться, а время реакции слишком шумное для BIS. Блок даёт
 * и градацию точности, и усреднённое время.
 */

export type ColorId =
  | 'red'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'purple'
  | 'orange'
  | 'teal'
  | 'pink'

/**
 * Порядок важен: первые цвета используются на низких уровнях.
 * Сначала идут максимально различимые между собой, ближе к концу -
 * те, что легче спутать (оранжевый рядом с красным и жёлтым).
 */
export const COLORS: readonly ColorId[] = [
  'red',
  'blue',
  'green',
  'yellow',
  'purple',
  'orange',
  'teal',
  'pink',
]

/** Оттенки подобраны так, чтобы читаться и на светлом, и на тёмном фоне. */
export const COLOR_HEX: Readonly<Record<ColorId, string>> = {
  red: '#E5484D',
  blue: '#3B82F6',
  green: '#22A06B',
  yellow: '#D9A404',
  purple: '#8B5CF6',
  orange: '#F0751F',
  teal: '#0FA5AC',
  pink: '#EC4899',
}

export interface StroopTrial {
  /** Какое слово написано */
  readonly word: ColorId
  /** Каким цветом написано - это и есть правильный ответ */
  readonly ink: ColorId
  /** Варианты ответа, всегда включают ink */
  readonly options: readonly ColorId[]
}

export interface StroopRound {
  readonly trials: readonly StroopTrial[]
  /** Лимит на одну пробу; не успел - проба засчитывается как ошибка */
  readonly timeLimitMs: number
}

export interface StroopAnswer {
  /** Ответ на каждую пробу; null - не успел */
  readonly picks: readonly (ColorId | null)[]
  /** Среднее время верных ответов, мс */
  readonly rtMs: number
}

export interface LevelSpec {
  readonly optionCount: number
  readonly trials: number
  /** Доля проб, где слово и цвет не совпадают - главный источник трудности */
  readonly incongruentShare: number
  readonly timeLimitMs: number
}

const LEVELS: readonly LevelSpec[] = [
  { optionCount: 2, trials: 6, incongruentShare: 0.5, timeLimitMs: 4000 },
  { optionCount: 3, trials: 6, incongruentShare: 0.6, timeLimitMs: 3800 },
  { optionCount: 3, trials: 6, incongruentShare: 0.7, timeLimitMs: 3600 },
  { optionCount: 4, trials: 6, incongruentShare: 0.7, timeLimitMs: 3400 },
  { optionCount: 4, trials: 8, incongruentShare: 0.8, timeLimitMs: 3200 },
  { optionCount: 4, trials: 8, incongruentShare: 0.8, timeLimitMs: 3000 },
  { optionCount: 5, trials: 8, incongruentShare: 0.85, timeLimitMs: 2800 },
  { optionCount: 5, trials: 8, incongruentShare: 0.9, timeLimitMs: 2650 },
  { optionCount: 6, trials: 8, incongruentShare: 0.9, timeLimitMs: 2500 },
  { optionCount: 6, trials: 10, incongruentShare: 1, timeLimitMs: 2350 },
  { optionCount: 7, trials: 10, incongruentShare: 1, timeLimitMs: 2200 },
  { optionCount: 8, trials: 10, incongruentShare: 1, timeLimitMs: 2050 },
]

/** Рамки лимита после применения личного темпа. */
export const MIN_TIME_LIMIT_MS = 1400
export const MAX_TIME_LIMIT_MS = 6000

/** Эталонное время ответа: типичная реакция в этой задаче. */
export const REFERENCE_RT_MS = 1000

export const MIN_LEVEL = 1
export const MAX_LEVEL = LEVELS.length
export const INITIAL_LEVEL = 3

export function specForLevel(level: number): LevelSpec {
  const index = Math.min(Math.max(level, MIN_LEVEL), MAX_LEVEL) - 1
  const spec = LEVELS[index]
  if (!spec) throw new Error(`Stroop: нет спецификации для уровня ${level}`)
  return spec
}

/**
 * @param pace личный темп игрока: 1 - как эталон, больше 1 - медленнее.
 *   Лимит времени растягивается под него, поэтому аккуратный, но
 *   неторопливый игрок не задыхается на высоких уровнях. Без этого
 *   лимит зависел бы только от уровня, а уровень растёт от точности,
 *   а не от скорости.
 */
export function generateRound(level: number, rng: Rng, pace = 1): StroopRound {
  const spec = specForLevel(level)
  const palette = COLORS.slice(0, spec.optionCount)

  const trials: StroopTrial[] = []
  for (let i = 0; i < spec.trials; i++) {
    const ink = rng.pick(palette)
    const incongruent = rng.next() < spec.incongruentShare

    // слово, отличное от цвета чернил - в этом вся суть пробы
    const others = palette.filter((c) => c !== ink)
    const word = incongruent && others.length > 0 ? rng.pick(others) : ink

    trials.push({ word, ink, options: rng.shuffle(palette) })
  }

  const timeLimitMs = Math.round(
    Math.min(Math.max(spec.timeLimitMs * pace, MIN_TIME_LIMIT_MS), MAX_TIME_LIMIT_MS),
  )

  return { trials, timeLimitMs }
}

export function evaluate(round: StroopRound, answer: StroopAnswer): RoundOutcome {
  const total = round.trials.length
  if (total === 0) return { correct: false, accuracy: 0, rtMs: answer.rtMs }

  let hits = 0
  round.trials.forEach((trial, i) => {
    if (answer.picks[i] === trial.ink) hits++
  })

  return {
    correct: hits === total,
    accuracy: hits / total,
    rtMs: answer.rtMs,
  }
}

/** Доля проб, где слово противоречит цвету. Для тестов и отладки. */
export function incongruentCount(round: StroopRound): number {
  return round.trials.filter((t) => t.word !== t.ink).length
}
